import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import config from '../config';

// ── Helpers ───────────────────────────────────────────────────────────────────

async function getIdToken() {
    const session = await fetchAuthSession();
    return session.tokens?.idToken?.toString();
}

function formatPostedAt(ts) {
    if (!ts) return '';
    const d = new Date(ts);
    return d.toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
    }) + ',  ' + d.toLocaleTimeString('en-US', {
        hour: 'numeric', minute: '2-digit', hour12: true,
    });
}

function formatEventDate(dateStr) {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
        weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    });
}

// ── Feed Card ─────────────────────────────────────────────────────────────────

function FeedCard({ item }) {
    const isCreate = item.feed_type === 'create';
    const label    = isCreate ? 'Event Created' : 'Event Updated';

    const eventPath    = `/events/${item.event_slug || item.event_id}`;
    const festivalPath = item.festival_slug ? `/festivals/${item.festival_slug}` : null;

    return (
        <div style={S.card}>
            {/* Card body: text left, image right */}
            <div style={S.cardInner}>

                {/* Left: text content */}
                <div style={S.cardText}>
                    {/* Header row */}
                    <div style={S.cardHeader}>
                        <span style={S.feedLabel}>{label}</span>
                        <span style={S.headerSep}> – Posted by </span>
                        <span style={S.postedBy}>{item.posted_by || 'SHARC User'}</span>
                        <span style={S.postedAt}>{formatPostedAt(item.ts)}</span>
                    </div>

                    {/* Event date + venue + location */}
                    <div style={S.eventDate}>{formatEventDate(item.event_date)}</div>
                    <div style={S.metaLine}>
                        <span style={S.venueName}>{item.venue_name}</span>
                        {(item.venue_city || item.state_name || item.country_name) && (
                            <span style={S.location}>
                                {' '}in {[item.venue_city, item.state_name, item.country_name].filter(Boolean).join(', ')}
                            </span>
                        )}
                    </div>

                    {/* Title (if present) */}
                    {item.event_title && (
                        <div style={S.eventTitle}>
                            <Link to={eventPath} style={S.purpleLink}>{item.event_title}</Link>
                        </div>
                    )}

                    {/* Festival */}
                    {festivalPath && (
                        <div style={S.metaLine}>
                            <strong>
                                <Link to={festivalPath} style={S.purpleLink}>{item.festival_name}</Link>
                            </strong>
                        </div>
                    )}

                    {/* Lineup */}
                    {item.lineup && (
                        <div style={S.lineup}>
                            <strong>{item.lineup}</strong>
                        </div>
                    )}

                    {/* View event link */}
                    <div style={{ marginTop: '10px' }}>
                        <Link to={eventPath} style={S.viewLink}>View Event →</Link>
                    </div>

                    {/* Action row */}
                    <div style={S.actionRow}>
                        <button style={S.actionBtn} disabled>Like</button>
                        <button style={S.actionBtn} disabled>Comment</button>
                        <button style={S.actionBtn} disabled>Share</button>
                    </div>
                </div>

                {/* Right: image */}
                {item.image_url && (
                    <div style={S.imageWrap}>
                        <Link to={eventPath}>
                            <div style={S.imageBox}>
                                <img
                                    src={item.image_url}
                                    alt={item.event_title || item.headliner_name || 'Event'}
                                    style={S.cardImage}
                                    onError={e => { e.target.parentNode.parentNode.style.display = 'none'; }}
                                />
                            </div>
                        </Link>
                    </div>
                )}
            </div>
        </div>
    );
}

// ── Feed Page ─────────────────────────────────────────────────────────────────

function Feed() {
    const [items,       setItems]       = useState([]);
    const [hasMore,     setHasMore]     = useState(false);
    const [offset,      setOffset]      = useState(0);
    const [loading,     setLoading]     = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [error,       setError]       = useState(null);

    // Filter state
    const [countries,   setCountries]   = useState([]);
    const [states,      setStates]      = useState([]);
    const [postedDate,  setPostedDate]  = useState('anytime');
    const [countryId,   setCountryId]   = useState('');
    const [stateId,     setStateId]     = useState('');

    // ── Fetch ─────────────────────────────────────────────────────────────────

    const fetchFeed = useCallback(async ({ reset = false } = {}) => {
        try {
            const token = await getIdToken();
            const params = {
                offset:      reset ? 0 : offset,
                posted_date: postedDate,
            };
            if (countryId) params.country_id = countryId;
            if (stateId)   params.state_id   = stateId;

            const res = await axios.get(`${config.API_URL}/feed`, {
                params,
                headers: { Authorization: `Bearer ${token}` },
            });

            const { items: newItems, has_more, offset: newOffset, filters } = res.data;

            if (reset) {
                setItems(newItems);
            } else {
                setItems(prev => [...prev, ...newItems]);
            }
            setHasMore(has_more);
            setOffset(newOffset);

            // Update filter options from response
            if (filters?.countries) setCountries(filters.countries);
            if (filters?.states)    setStates(filters.states);

        } catch (err) {
            setError('Failed to load feed.');
            console.error(err);
        } finally {
            setLoading(false);
            setLoadingMore(false);
        }
    }, [offset, postedDate, countryId, stateId]);

    // Initial load
    useEffect(() => {
        document.title = 'SHARC - Feed';
        setLoading(true);
        setItems([]);
        setOffset(0);
        fetchFeed({ reset: true });
        return () => { document.title = 'SHARC - Shows Archive'; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [postedDate, countryId, stateId]);

    // When country changes, reset state selection
    const handleCountryChange = (e) => {
        setCountryId(e.target.value);
        setStateId('');
    };

    const handleLoadMore = () => {
        setLoadingMore(true);
        fetchFeed({ reset: false });
    };

    // ── Render ────────────────────────────────────────────────────────────────

    return (
        <div style={S.page}>
            <div style={S.container}>

                {/* Page header */}
                <h1 style={S.heading}>Feed</h1>

                {/* Filter bar */}
                <div style={S.filterBar}>
                    {/* Posted Date */}
                    <div style={S.filterGroup}>
                        <label style={S.filterLabel}>Posted</label>
                        <select
                            style={S.select}
                            value={postedDate}
                            onChange={e => setPostedDate(e.target.value)}
                        >
                            <option value="anytime">Anytime</option>
                            <option value="24h">Past 24 Hours</option>
                            <option value="week">Past Week</option>
                            <option value="month">Past Month</option>
                        </select>
                    </div>

                    {/* Country */}
                    <div style={S.filterGroup}>
                        <label style={S.filterLabel}>Country</label>
                        <select
                            style={S.select}
                            value={countryId}
                            onChange={handleCountryChange}
                        >
                            <option value="">All Countries</option>
                            {countries.map(c => (
                                <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                        </select>
                    </div>

                    {/* Region — only shown when a country is selected */}
                    {countryId && (
                        <div style={S.filterGroup}>
                            <label style={S.filterLabel}>Region</label>
                            <select
                                style={S.select}
                                value={stateId}
                                onChange={e => setStateId(e.target.value)}
                            >
                                <option value="">All Regions</option>
                                {states.map(s => (
                                    <option key={s.id} value={s.id}>{s.name}</option>
                                ))}
                            </select>
                        </div>
                    )}
                </div>

                {/* Feed content */}
                {loading && (
                    <div style={S.message}>Loading feed...</div>
                )}

                {error && !loading && (
                    <div style={S.message}>No Results Found</div>
                )}

                {!loading && !error && items.length === 0 && (
                    <div style={S.message}>No Results Found</div>
                )}

                {!loading && items.map((item, idx) => (
                    <FeedCard key={`${item.feed_type}-${item.event_id}-${item.ts}-${idx}`} item={item} />
                ))}

                {/* Load more */}
                {hasMore && !loading && (
                    <div style={S.loadMoreWrap}>
                        <button
                            style={S.loadMoreBtn}
                            onClick={handleLoadMore}
                            disabled={loadingMore}
                        >
                            {loadingMore ? 'Loading...' : 'Load More'}
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
}

export default Feed;

// ── Styles ────────────────────────────────────────────────────────────────────

const S = {
    page: {
        backgroundColor: '#121212',
        minHeight: '100vh',
        padding: '32px 20px 60px',
        color: '#e0e0e0',
    },
    container: {
        maxWidth: '860px',
        margin: '0 auto',
    },
    heading: {
        fontSize: '26px',
        fontWeight: 'bold',
        color: '#e0e0e0',
        marginBottom: '24px',
    },

    // ── Filter bar ──
    filterBar: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '16px',
        marginBottom: '28px',
        alignItems: 'flex-end',
    },
    filterGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: '4px',
    },
    filterLabel: {
        fontSize: '11px',
        color: '#888',
        textTransform: 'uppercase',
        letterSpacing: '0.06em',
    },
    select: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#e0e0e0',
        fontSize: '14px',
        padding: '7px 12px',
        cursor: 'pointer',
        outline: 'none',
        minWidth: '160px',
    },

    // ── Card ──
    card: {
        backgroundColor: '#1e1e1e',
        border: '1px solid #2e2e2e',
        borderRadius: '8px',
        marginBottom: '20px',
        padding: '20px 22px',
    },
    cardInner: {
        display: 'flex',
        gap: '20px',
        alignItems: 'flex-start',
    },
    cardText: {
        flex: 1,
        minWidth: 0,
    },
    imageWrap: {
        flexShrink: 0,
        width: '160px',
    },
    imageBox: {
        position: 'relative',
        width: '160px',
        paddingBottom: '160px', // 1:1 square
        backgroundColor: '#161616',
        borderRadius: '6px',
        border: '1px solid #333',
        overflow: 'hidden',
    },
    cardImage: {
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        objectFit: 'contain',
        display: 'block',
    },

    // ── Card header ──
    cardHeader: {
        display: 'flex',
        flexWrap: 'wrap',
        alignItems: 'baseline',
        gap: '4px',
        marginBottom: '10px',
        fontSize: '15px',
    },
    feedLabel: {
        color: '#bb86fc',
        fontWeight: 'bold',
    },
    headerSep: {
        color: '#aaa',
    },
    postedBy: {
        color: '#bb86fc',
        fontWeight: '600',
    },
    postedAt: {
        color: '#777',
        fontSize: '13px',
        marginLeft: '8px',
    },

    // ── Card body ──
    eventDate: {
        fontSize: '15px',
        fontWeight: '600',
        color: '#e0e0e0',
        marginBottom: '4px',
    },
    metaLine: {
        fontSize: '14px',
        color: '#ccc',
        marginBottom: '3px',
    },
    venueName: {
        color: '#e0e0e0',
    },
    location: {
        color: '#aaa',
    },
    eventTitle: {
        fontSize: '14px',
        marginBottom: '3px',
    },
    lineup: {
        fontSize: '15px',
        color: '#e0e0e0',
        marginTop: '10px',
        lineHeight: '1.5',
    },
    purpleLink: {
        color: '#bb86fc',
        textDecoration: 'none',
    },
    viewLink: {
        color: '#bb86fc',
        textDecoration: 'none',
        fontSize: '13px',
    },

    // ── Action row ──
    actionRow: {
        display: 'flex',
        gap: '16px',
        marginTop: '14px',
        paddingTop: '12px',
        borderTop: '1px solid #2e2e2e',
    },
    actionBtn: {
        background: 'transparent',
        border: 'none',
        color: '#777',
        fontSize: '14px',
        cursor: 'not-allowed',
        padding: 0,
    },

    // ── Misc ──
    message: {
        color: '#888',
        fontSize: '15px',
        textAlign: 'center',
        padding: '60px 0',
    },
    errorMsg: {
        color: '#cf6679',
        fontSize: '15px',
        textAlign: 'center',
        padding: '40px 0',
    },
    loadMoreWrap: {
        textAlign: 'center',
        marginTop: '24px',
    },
    loadMoreBtn: {
        backgroundColor: '#2c2c2c',
        border: '1px solid #444',
        borderRadius: '4px',
        color: '#e0e0e0',
        fontSize: '14px',
        padding: '10px 28px',
        cursor: 'pointer',
    },
};
